import { i18n } from 'i18n';
import {
  CreateProjectCommand,
  FileParameter,
  MineModelDto,
  PagedListOfProjectDto,
  ProjectDto,
  SetCurrencyPreferenceCommand,
  SetHistoricalBinderPercentageCommand,
  SetProjectArchivedCommand,
  SetProjectLicenceExpiryDateCommand,
  UpdateProjectCommand
} from './api';
import {
  PageParams,
  makeCreateMutation,
  makeDeleteMutation,
  makeGetQuery,
  makeInfiniteListQuery,
  makeListQuery,
  makeUpdateMutation
} from './useApi';
import {
  CLIENT_TEAMS_KEY,
  CLIENT_TEAM_KEY
} from './useTeams';
import { ME_KEY } from './useTeamRoles';

export const PROJECTS_KEY = 'projects';
export interface ProjectsQueryParams extends PageParams {
  searchTerm?: string | null;
  clientId?: string | null;
  projectType?: string | null;
  includeArchived?: boolean;
}

export const useProjectsQuery = makeListQuery<ProjectsQueryParams, PagedListOfProjectDto>(
  PROJECTS_KEY,
  (api, params) => () => api.projects.list(params.page, params.pageSize, params.searchTerm, params.clientId, params.projectType, params.includeArchived),
);

export const useInfiniteProjectsQuery = makeInfiniteListQuery<null, ProjectDto>(
  PROJECTS_KEY,
  (api) => ({
    pageParam = { page: 1, pageSize: 12 },
  }) => api.projects.list(pageParam.page, pageParam.pageSize),
);

const PROJECT_KEY = 'project';
export const useProjectQuery = makeGetQuery<ProjectDto, string>(
  PROJECT_KEY,
  (api, projectId) => () => api.projects.get(projectId),
);

const PROJECT_CLIENT_KEY = 'project_client';
export const useProjectClientQuery = makeGetQuery<string, string>(
  PROJECT_CLIENT_KEY,
  (api, projectId) => () => api.projects.getClientId(projectId),
);

export interface ProjectWithMineModel {
  project: ProjectDto;
  mineModel: MineModelDto | null;
}

export interface ProjectMutation {
  clientId: string,
  command: CreateProjectCommand | UpdateProjectCommand
}

export const useCreateProjectMutation = makeCreateMutation<string, ProjectMutation>(
  PROJECTS_KEY,
  (api) => (mutation) => {
    const command = mutation.command as CreateProjectCommand;
    // If it's a P&C project, use the P&C endpoint
    if (command.isPatersonAndCookeProject) {
      return api.projects.createPcProject(command);
    }
    return api.projects.create(mutation.clientId, command as CreateProjectCommand);
  },
  () => i18n._('Project').concat(i18n._('successfully created')),
  [CLIENT_TEAMS_KEY, CLIENT_TEAM_KEY, ME_KEY],
);

export const useUpdateProjectMutation = makeUpdateMutation<void, ProjectMutation>(
  PROJECT_KEY, PROJECTS_KEY,
  (api, projectId) => (mutation) => api.projects.update(projectId, mutation.command as UpdateProjectCommand),
  () => i18n._('Project ').concat(i18n._('successfully updated')),
);

export const useDeleteProjectMutation = makeDeleteMutation<void, ProjectDto>(
  PROJECT_KEY, PROJECTS_KEY,
  (api) => (project) => api.projects.delete(project.entityId),
  () => i18n._('Project').concat(i18n._('successfully deleted')),
  (mutation) => mutation.entityId,
);

export interface ArchiveProjectMutation {
  project: ProjectDto;
  isArchived: boolean;
}

export const useArchiveProjectMutation = makeCreateMutation<void, ArchiveProjectMutation>(
  PROJECTS_KEY,
  (api) => ({ project, isArchived }) => api.projects.setArchived(project.entityId, { isArchived }),
  ({ isArchived }) => (isArchived ? i18n._('Project archived') : i18n._('Project restored')),
  [PROJECT_KEY],
);

export const useUpdateProjectImageMutation = makeUpdateMutation<void, FileParameter>(
  PROJECT_KEY, PROJECTS_KEY,
  (api, projectId) => (file) => api.projects.updateProjectImage(projectId, file),
  () => i18n._('Uploaded image successfully updated'),
);

export interface UpdateLicenceExpiryMutation {
  projectId: string;
  command: SetProjectLicenceExpiryDateCommand,
}

export const useUpdateLicenceExpiryMutation = makeUpdateMutation<void, UpdateLicenceExpiryMutation>(
  PROJECT_KEY, PROJECTS_KEY,
  (api, projectId) => (mutation) => api.projects.updateProjectLicenceExpiryDate(projectId, mutation.command),
  () => i18n._('Licence Expiry Date updated'),
);

export const useSetCurrencyPreferenceMutation = makeUpdateMutation<void, SetCurrencyPreferenceCommand>(
  PROJECT_KEY, PROJECTS_KEY,
  (api, projectId) => (mutation) => api.projects.setCurrencyPreference(projectId, mutation),
  () => i18n._('Currency preference updated successfully'),
);

export const useSetHistoricalBinderPercentageMutation = makeUpdateMutation<void, SetHistoricalBinderPercentageCommand>(
  PROJECT_KEY, PROJECTS_KEY,
  (api, projectId) => (mutation) => api.projects.setHistoricalBinderPercentage(projectId, mutation),
  () => i18n._('Historical Binder Percentage updated successfully'),
);

export const useSetProjectArchivedMutation = makeUpdateMutation<void, SetProjectArchivedCommand>(
  PROJECT_KEY, PROJECTS_KEY,
  (api, projectId) => (mutation) => api.projects.setArchived(projectId, mutation),
  (mutation) => (mutation.isArchived ? i18n._('Project archived') : i18n._('Project restored')),
  [PROJECTS_KEY, PROJECT_KEY],
);

export interface SetStopeNoteTemplateMutation {
  projectId: string;
  templateId: string | null;
}

// Designate (or clear, with null) the custom-report template used as this project's stope note. Also
// invalidates the stope-note-template query so the toggle + generation gate refresh.
export const useSetStopeNoteTemplateMutation = makeUpdateMutation<void, SetStopeNoteTemplateMutation>(
  PROJECT_KEY, PROJECT_KEY,
  (api) => (mutation) => api.projects.setStopeNoteTemplateId(
    mutation.projectId,
    { stopeNoteTemplateId: mutation.templateId ?? undefined },
  ),
  () => i18n._('Stope note template updated'),
  [PROJECTS_KEY, 'custom-report-stope-note-template'],
);
